/**
 * Deployment Grid Renderer
 *
 * PixiJS-based rendering system for the hexagonal deployment grid.
 * Handles grid rendering, zone overlays, and interactive highlights.
 */

import * as PIXI from 'pixi.js';
import { HexGrid } from '@drawn-of-war/shared';
import type { AxialCoordinate, HexGridConfig } from '@drawn-of-war/shared';
import { TerrainRenderer } from '../../services/terrain/terrain-renderer';
import { ThemeAssetLoader } from '../../services/terrain/theme-asset-loader';
import { CYBER_ARENA_THEME } from '../../config/themes/cyber-arena';
import { BattlePhase } from '../../types/terrain';
import { CameraManager, Point } from '../../services/camera';
import { RemovalGestureController, RemovalGestureCallbacks } from './RemovalGestureController';
import { RemovalAnimationController } from './RemovalAnimationController';
import { REMOVAL_HAPTICS } from '../../services/haptic-feedback';
import { applyDesaturation } from '../../utils/pixi-filters';
import { DEFAULT_VISUAL_SETTINGS } from '../../config/visual-settings';
import type { HexTileAssignment } from '@drawn-of-war/shared';
import {
  BASE_TILES, OVERLAY_TILES, getAllTilePaths,
  type BaseTileId, type OverlayTileId
} from '../BattlefieldEditor/tile-registry';

/**
 * Highlight states for hex interaction feedback
 */
export enum HighlightState {
  NONE = 'none',
  HOVER = 'hover',
  VALID = 'valid',
  INVALID = 'invalid',
  SELECTED = 'selected',
  RANGE = 'range'
}

/**
 * Deployment zone configuration
 */
export interface DeploymentZone {
  playerId: 'player1' | 'player2';
  hexes: AxialCoordinate[];
  color: number; // e.g., 0x3498db for blue
  alpha: number; // e.g., 0.2 for 20% opacity
}

/**
 * Creature metadata for interactivity tracking
 */
export interface CreatureMetadata {
  creatureId: string;
  playerId: 'player1' | 'player2';
  hex: AxialCoordinate;
}

/**
 * Configuration for the hex grid renderer
 */
export interface HexGridRendererConfig {
  canvasWidth: number;
  canvasHeight: number;
  hexGridConfig: HexGridConfig;
  showCoordinates?: boolean;
  onHexHover?: (hex: AxialCoordinate | null) => void;
  onHexClick?: (hex: AxialCoordinate) => void;
  /** Current player ID - used to determine which creatures are interactive */
  currentPlayerId?: 'player1' | 'player2';
  /** Callback when a creature sprite is clicked (for repositioning) */
  onCreatureClick?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Deployment zone configuration (default: left-vs-right PvP zones) */
  deploymentZoneConfig?: {
    orientation: 'left_vs_right' | 'top_vs_bottom';
    playerDeployCols?: number;
    /** Explicit per-player column ranges (overrides playerDeployCols and defaults) */
    player1ColRange?: { min: number; max: number };
    player2ColRange?: { min: number; max: number };
    /** For top_vs_bottom: number of rows for player deploy zone (bottom) */
    playerDeployRows?: number;
    /** For top_vs_bottom: number of rows for enemy deploy zone (top) */
    enemyDeployRows?: number;
    gridHeight?: number;
    gridWidth?: number;
  };
  /** Callback when a creature sprite is right-clicked (for removal) */
  onCreatureRightClick?: (creatureId: string, hex: AxialCoordinate) => void;

  /**
   * Custom background image URL (overrides parallax terrain background).
   * Used for challenge maps with hand-positioned backgrounds.
   */
  backgroundImageOverride?: string;
  /** Background center X offset from grid center in world pixels */
  bgOffsetX?: number;
  /** Background center Y offset from grid center in world pixels */
  bgOffsetY?: number;
  /** Background scale as fraction of natural texture size (1.0 = original) */
  bgScale?: number;
  /** Background brightness (0–2, default 1.0) */
  bgBrightness?: number;
  /** Background contrast (0–2, default 1.0) */
  bgContrast?: number;
  /** Background saturation (0–2, default 1.0) */
  bgSaturation?: number;
  /** Background hue rotation in degrees (-180–180, default 0) */
  bgHue?: number;

  // Mobile tap-and-hold removal callbacks
  /** Callback when hold is detected (500ms threshold passed) */
  onRemovalHoldDetected?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Callback for removal progress (0-1, called each frame during removal) */
  onRemovalProgress?: (progress: number) => void;
  /** Callback when removal completes (3 second hold) */
  onRemovalComplete?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Callback when removal is cancelled (user released early or moved) */
  onRemovalCancelled?: () => void;
}

/**
 * Main hex grid renderer using PixiJS
 */
export class HexGridRenderer {
  protected app: PIXI.Application;
  protected hexGrid: HexGrid;
  private config: HexGridRendererConfig;

  private gridContainer: PIXI.Container;
  private highlightLayer: PIXI.Container;
  private coordinateLayer: PIXI.Container;
  private creatureLayer: PIXI.Container;

  /** World container - holds all game content, camera transforms apply here */
  protected worldContainer: PIXI.Container;

  /** Camera manager for zoom/pan controls */
  protected cameraManager: CameraManager | null = null;

  private hexGraphics: Map<string, PIXI.Graphics>;
  private highlightGraphics: Map<string, PIXI.Graphics>;
  private coordinateTexts: Map<string, PIXI.Text>;

  /**
   * Creature sprite storage
   * Protected to allow derived classes (CombatGridRenderer) to access for unified sprite lookup
   * Maps hex hash → PIXI.Container for static sprites
   */
  protected creatureSprites: Map<string, PIXI.Container>;

  /**
   * Creature metadata for interactivity
   * Maps hex hash → CreatureMetadata (creatureId, playerId, hex)
   */
  protected creatureMetadata: Map<string, CreatureMetadata>;

  private currentHoverHex: AxialCoordinate | null = null;
  private showCoordinates: boolean = false;
  private isInitialized: boolean = false;
  private isDestroyed: boolean = false;

  protected terrainRenderer: TerrainRenderer | null = null;
  /** Custom background image sprite (challenge maps) — stored so loadTiles can hide it */
  protected customBgSprite: PIXI.Sprite | null = null;

  /**
   * URL for a background image that persists BEHIND hex tiles.
   * Unlike backgroundImageOverride (hidden when tiles load), this image stays
   * visible in the gaps between hex tiles, replacing the black void.
   * Set this before calling loadTiles().
   */
  public tileBackgroundUrl: string | null = null;

  /** Deployment zone overlay graphics — stored so they can be cleared when combat starts */
  private zoneOverlays: PIXI.Graphics[] = [];

  /** Controller for tap-and-hold removal gesture */
  private removalGestureController: RemovalGestureController | null = null;
  /** Controller for removal animations (wiggle + timer circle) */
  private removalAnimationController: RemovalAnimationController | null = null;

  constructor(config: HexGridRendererConfig) {
    this.config = config;
    this.hexGrid = new HexGrid(config.hexGridConfig);
    this.hexGraphics = new Map();
    this.highlightGraphics = new Map();
    this.coordinateTexts = new Map();
    this.creatureSprites = new Map();
    this.creatureMetadata = new Map();
    this.showCoordinates = config.showCoordinates || false;

    // Initialize PixiJS application
    this.app = new PIXI.Application();

    // World container holds all game content - camera transforms apply here
    this.worldContainer = new PIXI.Container();

    this.gridContainer = new PIXI.Container();
    this.highlightLayer = new PIXI.Container();
    this.coordinateLayer = new PIXI.Container();
    this.creatureLayer = new PIXI.Container();
  }

  /**
   * Initialize the renderer asynchronously
   */
  async init(): Promise<HTMLCanvasElement> {
    // If already destroyed during initialization, abort
    if (this.isDestroyed) {
      throw new Error('Renderer was destroyed before initialization completed');
    }

    await this.app.init({
      width: this.config.canvasWidth,
      height: this.config.canvasHeight,
      backgroundColor: 0x1a1a1a,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true
    });

    // Check again after async operation
    if (this.isDestroyed) {
      // Clean up app if it was initialized but we're now destroyed
      this.app.destroy(true, { children: true, texture: true });
      throw new Error('Renderer was destroyed during initialization');
    }

    // Add world container to stage - all game content goes inside this
    // Camera transforms (zoom/pan) apply to worldContainer
    this.app.stage.addChild(this.worldContainer);

    // Setup layer hierarchy inside worldContainer (order matters for rendering)
    this.worldContainer.addChild(this.gridContainer);
    this.worldContainer.addChild(this.highlightLayer);
    this.worldContainer.addChild(this.creatureLayer);
    this.worldContainer.addChild(this.coordinateLayer);

    // Initialize camera manager for zoom/pan controls
    this.cameraManager = new CameraManager(this.worldContainer, {
      canvasWidth: this.config.canvasWidth,
      canvasHeight: this.config.canvasHeight,
    });

    // Render the static grid
    this.renderStaticGrid();

    // Setup interaction
    this.setupInteraction();

    // Suppress browser context menu on canvas (for right-click removal)
    this.app.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // Initialize terrain renderer
    const gridBounds = this.calculateGridBoundsForTerrain();

    this.terrainRenderer = new TerrainRenderer(
      CYBER_ARENA_THEME,
      gridBounds,
      (hex) => this.hexToPixel(hex),
      (center) => this.getHexCornersAtPixel(center)
    );

    // Load theme assets (textures)
    const assetLoader = new ThemeAssetLoader();
    let textureMap: Map<string, PIXI.Texture> | undefined;

    try {
      const loadedTextures = await assetLoader.loadTheme(CYBER_ARENA_THEME, (progress) => {
        console.log(`[HexGridRenderer] Loading terrain assets: ${(progress * 100).toFixed(0)}%`);
      });

      // Convert loaded textures to Map<url, texture> for terrain renderer
      textureMap = new Map<string, PIXI.Texture>();
      textureMap.set(CYBER_ARENA_THEME.assets.farBackground, loadedTextures.farBackground);
      textureMap.set(CYBER_ARENA_THEME.assets.midBackground, loadedTextures.midBackground);
      textureMap.set(CYBER_ARENA_THEME.assets.nearBackground, loadedTextures.nearBackground);
      textureMap.set(CYBER_ARENA_THEME.assets.fogParticle, loadedTextures.fogParticle);
      textureMap.set(CYBER_ARENA_THEME.assets.dustParticle, loadedTextures.dustParticle);

      console.log('[HexGridRenderer] Theme assets loaded successfully');
    } catch (error) {
      console.warn('[HexGridRenderer] Failed to load theme assets, using placeholders:', error);
      // textureMap remains undefined, terrain will use placeholders
    }

    // Initialize terrain with loaded textures (or placeholders if load failed)
    await this.terrainRenderer.initialize(textureMap);

    // Add terrain containers to worldContainer in correct order
    // Background container at index 0 (below grid)
    this.worldContainer.addChildAt(this.terrainRenderer.getBackgroundContainer(), 0);

    // Effects container after grid (index 2, after gridContainer shifts to 1)
    this.worldContainer.addChildAt(this.terrainRenderer.getEffectsContainer(), 2);

    // Dust container after creatures (index 5, above creatureLayer which is now at index 4)
    this.worldContainer.addChildAt(this.terrainRenderer.getDustContainer(), 5);

    console.log('[HexGridRenderer] Terrain renderer integrated successfully');

    // Apply background desaturation to improve visual hierarchy
    const desatConfig = DEFAULT_VISUAL_SETTINGS.backgroundDesaturation;
    if (desatConfig.enabled) {
      const bgContainer = this.terrainRenderer.getBackgroundContainer();
      applyDesaturation(bgContainer, desatConfig.strength);
      console.log(`[HexGridRenderer] Applied ${(desatConfig.strength * 100).toFixed(0)}% background desaturation`);
    }

    // Custom background image (challenge maps with hand-positioned backgrounds)
    if (this.config.backgroundImageOverride && this.config.bgScale != null) {
      try {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        bgImg.src = this.config.backgroundImageOverride;
        await new Promise<void>((resolve, reject) => {
          bgImg.onload = () => resolve();
          bgImg.onerror = () => reject(new Error(`Failed to load custom bg: ${this.config.backgroundImageOverride}`));
        });

        const bgTexture = PIXI.Texture.from(bgImg);
        const bgSprite = new PIXI.Sprite(bgTexture);

        // bgScale = fraction of natural texture size — apply directly
        bgSprite.scale.set(this.config.bgScale);
        bgSprite.anchor.set(0.5, 0.5);

        // Position at grid center + offset
        const gridBounds = this.calculateGridBounds();
        const gridOffset = this.getGridOffset();
        const gridCenterX = gridOffset.x + gridBounds.minX + gridBounds.width / 2;
        const gridCenterY = gridOffset.y + gridBounds.minY + gridBounds.height / 2;
        bgSprite.x = gridCenterX + (this.config.bgOffsetX ?? 0);
        bgSprite.y = gridCenterY + (this.config.bgOffsetY ?? 0);

        // Apply color adjustments if any are non-default
        const brightness = this.config.bgBrightness ?? 1.0;
        const contrast = this.config.bgContrast ?? 1.0;
        const saturation = this.config.bgSaturation ?? 1.0;
        const hue = this.config.bgHue ?? 0;
        const hasColorAdjustments = brightness !== 1.0 || contrast !== 1.0 || saturation !== 1.0 || hue !== 0;

        if (hasColorAdjustments) {
          const colorFilter = new PIXI.ColorMatrixFilter();
          colorFilter.reset();
          colorFilter.brightness(brightness, true);
          colorFilter.contrast(contrast, true);
          colorFilter.saturate(saturation, true);
          if (hue !== 0) {
            colorFilter.hue(hue, true);
          }
          bgSprite.filters = [colorFilter];
          console.log('[HexGridRenderer] Applied color adjustments:', { brightness, contrast, saturation, hue });
        }

        // Hide the parallax terrain background, use custom sprite instead
        this.terrainRenderer.getBackgroundContainer().visible = false;
        this.customBgSprite = bgSprite;
        this.worldContainer.addChildAt(bgSprite, 1);

        // Constrain zoom/pan so the background always fills the viewport
        const bgWorldWidth = bgTexture.width * this.config.bgScale;
        const bgWorldHeight = bgTexture.height * this.config.bgScale;
        const minZoomX = this.config.canvasWidth / bgWorldWidth;
        const minZoomY = this.config.canvasHeight / bgWorldHeight;
        const bgMinZoom = Math.max(minZoomX, minZoomY);
        if (this.cameraManager) {
          this.cameraManager.updateConfig({
            minZoom: bgMinZoom,
            bgWorldBounds: {
              left: bgSprite.x - bgWorldWidth / 2,
              right: bgSprite.x + bgWorldWidth / 2,
              top: bgSprite.y - bgWorldHeight / 2,
              bottom: bgSprite.y + bgWorldHeight / 2,
            },
          });
          if (this.cameraManager.getZoom() < bgMinZoom) {
            this.cameraManager.setZoom(bgMinZoom);
            this.cameraManager.applyTransform();
          }
        }

        console.log('[HexGridRenderer] Custom background loaded:', {
          bgScale: this.config.bgScale,
          bgOffsetX: this.config.bgOffsetX,
          bgOffsetY: this.config.bgOffsetY,
          naturalSize: `${bgTexture.width}x${bgTexture.height}`,
          renderedSize: `${bgWorldWidth}x${bgWorldHeight}`,
          position: `(${bgSprite.x.toFixed(0)}, ${bgSprite.y.toFixed(0)})`,
          bgMinZoom: bgMinZoom.toFixed(3),
        });
      } catch (err) {
        console.warn('[HexGridRenderer] Failed to load custom background:', err);
      }
    }

    // Setup terrain animation loop
    this.app.ticker.add(() => {
      if (this.terrainRenderer) {
        // deltaMS is in milliseconds, convert to seconds for terrain renderer
        this.terrainRenderer.update(this.app.ticker.deltaMS / 1000);
      }
    });

    // Initialize removal animation controller
    this.removalAnimationController = new RemovalAnimationController(
      undefined, // default wiggle config
      undefined, // default timer config
      this.hexGrid.hexSize // pass hex size for circle radius
    );

    // Initialize removal gesture controller if mobile removal callbacks provided
    if (this.config.onRemovalHoldDetected || this.config.onRemovalComplete) {
      const callbacks: RemovalGestureCallbacks = {
        onHoldDetected: (creatureId, hex) => {
          // Trigger haptic feedback
          REMOVAL_HAPTICS.holdDetected();

          // Start wiggle + timer circle animation
          const hexHash = this.hexGrid.hash(hex);
          const container = this.creatureSprites.get(hexHash);
          if (container) {
            this.removalAnimationController?.startWiggle(container);
          }

          // Notify parent
          this.config.onRemovalHoldDetected?.(creatureId, hex);
        },

        onRemovalProgress: (progress) => {
          // Update timer circle
          this.removalAnimationController?.updateTimerProgress(progress);

          // Notify parent
          this.config.onRemovalProgress?.(progress);
        },

        onRemovalComplete: (creatureId, hex) => {
          // Trigger haptic feedback
          REMOVAL_HAPTICS.removalComplete();

          // Stop animations
          this.removalAnimationController?.stopWiggle();

          // Notify parent
          this.config.onRemovalComplete?.(creatureId, hex);
        },

        onRemovalCancelled: () => {
          // Trigger haptic feedback
          REMOVAL_HAPTICS.cancelled();

          // Stop animations
          this.removalAnimationController?.stopWiggle();

          // Notify parent
          this.config.onRemovalCancelled?.();
        },

        onSwitchToDrag: (creatureId, hex) => {
          // Stop animations and trigger drag
          this.removalAnimationController?.stopWiggle();

          // Notify parent to start drag/reposition
          this.config.onCreatureClick?.(creatureId, hex);
        },
      };

      this.removalGestureController = new RemovalGestureController(callbacks);
      console.log('[HexGridRenderer] Removal gesture controller initialized');
    }

    // Mark as initialized
    this.isInitialized = true;

    return this.app.canvas;
  }

  /** Cached grid offset for consistent coordinate transformations */
  private cachedGridOffset: { x: number; y: number } | null = null;

  /**
   * Get the grid offset used for centering the grid on canvas
   * Uses cached value for performance - call invalidateGridOffset() if config changes
   */
  public getGridOffset(): { x: number; y: number } {
    if (this.cachedGridOffset) {
      return this.cachedGridOffset;
    }

    const isIsometric = this.config.hexGridConfig.projection === 'isometric';
    const bounds = this.calculateGridBounds();
    const verticalBias = isIsometric ? 50 : 0;

    this.cachedGridOffset = {
      x: (this.config.canvasWidth - bounds.width) / 2 - bounds.minX,
      y: (this.config.canvasHeight - bounds.height) / 2 - bounds.minY + verticalBias
    };

    return this.cachedGridOffset;
  }

  /**
   * Invalidate cached grid offset (call when canvas size or projection changes)
   */
  protected invalidateGridOffset(): void {
    this.cachedGridOffset = null;
  }

  /**
   * Get the grid bounds (public accessor)
   * Returns the actual rendered bounding box of the grid after projection
   */
  public getGridBounds(): { width: number; height: number; minX: number; minY: number } {
    return this.calculateGridBounds();
  }

  /**
   * Hide the default terrain background (cyber-arena theme).
   * Used when a custom map visual background is loaded instead.
   */
  public hideTerrainBackground(): void {
    if (this.terrainRenderer) {
      this.terrainRenderer.getBackgroundContainer().visible = false;
    }
  }

  /**
   * Load and render tile textures for the deployment grid.
   * Hides the default cyber-arena background and replaces it with map tiles.
   */
  public async loadTiles(tiles: Record<string, HexTileAssignment>): Promise<void> {
    if (!tiles || Object.keys(tiles).length === 0) return;

    // Preload all tile textures
    const paths = getAllTilePaths();
    const tileTextureCache = new Map<string, PIXI.Texture>();
    for (const path of paths) {
      try {
        const texture = await PIXI.Assets.load<PIXI.Texture>(path);
        tileTextureCache.set(path, texture);
      } catch (err) {
        console.warn(`[HexGridRenderer] Failed to load tile: ${path}`, err);
      }
    }

    // Hide the default cyber-arena background and terrain effects
    this.hideTerrainBackground();
    if (this.terrainRenderer) {
      this.terrainRenderer.getEffectsContainer().visible = false;
      this.terrainRenderer.getDustContainer().visible = false;
    }

    // If a custom background image exists, move it to the very back (behind tiles)
    // instead of hiding it — it fills the gaps between hex tiles.
    let bgLayerCount = 0;
    if (this.customBgSprite) {
      this.worldContainer.removeChild(this.customBgSprite);
      this.worldContainer.addChildAt(this.customBgSprite, 0);
      bgLayerCount = 1;
    }

    // Load a persistent background behind tiles if configured (legacy path)
    if (!this.customBgSprite && this.tileBackgroundUrl) {
      await this.loadTileBackground();
      bgLayerCount = 1;
    }

    // Create tile layers
    const baseTileLayer = new PIXI.Container();
    const overlayTileLayer = new PIXI.Container();

    // Insert tile layers after the background (if present, it's at index 0)
    this.worldContainer.addChildAt(baseTileLayer, bgLayerCount);
    this.worldContainer.addChildAt(overlayTileLayer, bgLayerCount + 1);

    const offset = this.getGridOffset();

    // Render base tiles (hex-clipped)
    for (const [key, assignment] of Object.entries(tiles)) {
      const baseDef = BASE_TILES[assignment.baseTile as BaseTileId];
      if (!baseDef) continue;

      const texture = tileTextureCache.get(baseDef.path);
      if (!texture) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexGrid.toPixel(hex);

      const sprite = new PIXI.Sprite(texture);
      const imgSize = this.hexGrid.hexSize * 2.2;
      sprite.width = imgSize;
      sprite.height = imgSize;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x + offset.x;
      sprite.y = pixel.y + offset.y;

      // Hex polygon mask
      const mask = new PIXI.Graphics();
      mask.poly(this.getHexVertices(this.hexGrid.hexSize));
      mask.fill({ color: 0xffffff });
      mask.x = pixel.x + offset.x;
      mask.y = pixel.y + offset.y;

      sprite.mask = mask;
      baseTileLayer.addChild(mask);
      baseTileLayer.addChild(sprite);
    }

    // Collect and Y-sort overlays
    const overlays: Array<{
      hex: AxialCoordinate;
      overlayId: OverlayTileId;
      pixelY: number;
    }> = [];

    for (const [key, assignment] of Object.entries(tiles)) {
      if (!assignment.overlay) continue;
      const overlayDef = OVERLAY_TILES[assignment.overlay as OverlayTileId];
      if (!overlayDef) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexGrid.toPixel(hex);
      overlays.push({ hex, overlayId: assignment.overlay as OverlayTileId, pixelY: pixel.y });
    }

    overlays.sort((a, b) => a.pixelY - b.pixelY);

    for (const { hex, overlayId } of overlays) {
      const overlayDef = OVERLAY_TILES[overlayId];
      const texture = tileTextureCache.get(overlayDef.path);
      if (!texture) continue;

      const pixel = this.hexGrid.toPixel(hex);
      const sprite = new PIXI.Sprite(texture);
      const size = this.hexGrid.hexSize * 2 * overlayDef.scale;
      sprite.width = size;
      sprite.height = size;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x + offset.x;
      sprite.y = pixel.y + offset.y + overlayDef.offsetY;

      overlayTileLayer.addChild(sprite);
    }

    this.constrainCameraToGrid();

    console.log(`[HexGridRenderer] Loaded ${Object.keys(tiles).length} tiles, ${overlays.length} overlays`);
  }

  // ─── Progressive Loading Methods ───────────────────────────────────────

  /** Theme-based skeleton hex fill colors for progressive loading */
  private static readonly SKELETON_COLORS: Record<string, { fill: number; alpha: number }> = {
    'forest-glade':    { fill: 0x223c22, alpha: 0.6 },
    'ancient-ruins':   { fill: 0x322d28, alpha: 0.6 },
    'lava-wasteland':  { fill: 0x502d14, alpha: 0.6 },
    'frozen-tundra':   { fill: 0x1e3246, alpha: 0.6 },
    'desert-oasis':    { fill: 0x4a3518, alpha: 0.6 },
    'crystal-cavern':  { fill: 0x2a2050, alpha: 0.6 },
    'cyber-arena':     { fill: 0x141928, alpha: 0.6 },
  };

  /**
   * Render theme-colored filled hexes for every grid cell as a skeleton placeholder.
   * Inserts as the first child of worldContainer so real tiles can layer on top.
   */
  public renderSkeletonHexes(themeId: string): PIXI.Container {
    const skeletonContainer = new PIXI.Container();
    const colors = HexGridRenderer.SKELETON_COLORS[themeId] ?? { fill: 0x1a1a2e, alpha: 0.6 };
    const { width, height } = this.hexGrid.getConfig();
    const offset = this.getGridOffset();
    const vertices = this.getHexVertices(this.hexGrid.hexSize);

    for (let q = 0; q < width; q++) {
      for (let r = 0; r < height; r++) {
        const pixel = this.hexGrid.toPixel({ q, r });
        const g = new PIXI.Graphics();
        g.poly(vertices);
        g.fill({ color: colors.fill, alpha: colors.alpha });
        g.x = pixel.x + offset.x;
        g.y = pixel.y + offset.y;
        skeletonContainer.addChild(g);
      }
    }

    // Insert as the very first child so tiles render on top
    this.worldContainer.addChildAt(skeletonContainer, 0);
    return skeletonContainer;
  }

  /**
   * Load tiles identically to loadTiles(), but set all sprites to alpha:0
   * for crossfade animation. Returns sprite references for animation.
   */
  public async loadTilesForCrossfade(tiles: Record<string, HexTileAssignment>): Promise<{
    baseTileSprites: PIXI.Sprite[];
    overlaySprites: PIXI.Sprite[];
  }> {
    const baseTileSprites: PIXI.Sprite[] = [];
    const overlaySprites: PIXI.Sprite[] = [];

    if (!tiles || Object.keys(tiles).length === 0) {
      return { baseTileSprites, overlaySprites };
    }

    // Preload all tile textures
    const paths = getAllTilePaths();
    const tileTextureCache = new Map<string, PIXI.Texture>();
    for (const path of paths) {
      try {
        const texture = await PIXI.Assets.load<PIXI.Texture>(path);
        tileTextureCache.set(path, texture);
      } catch (err) {
        console.warn(`[HexGridRenderer] Failed to load tile for crossfade: ${path}`, err);
      }
    }

    // Hide the default cyber-arena background and terrain effects
    this.hideTerrainBackground();
    if (this.terrainRenderer) {
      this.terrainRenderer.getEffectsContainer().visible = false;
      this.terrainRenderer.getDustContainer().visible = false;
    }

    // If a custom background image exists, move it behind tiles and start at alpha 0
    let bgLayerCount = 0;
    if (this.customBgSprite) {
      this.worldContainer.removeChild(this.customBgSprite);
      this.worldContainer.addChildAt(this.customBgSprite, 0);
      this.customBgSprite.alpha = 0;
      baseTileSprites.push(this.customBgSprite);
      bgLayerCount = 1;
    }

    // Load persistent background behind tiles if configured (legacy path)
    if (!this.customBgSprite && this.tileBackgroundUrl) {
      await this.loadTileBackground();
      bgLayerCount = 1;
    }

    // Create tile layers
    const baseTileLayer = new PIXI.Container();
    const overlayTileLayer = new PIXI.Container();

    // Insert tile layers after the background
    this.worldContainer.addChildAt(baseTileLayer, bgLayerCount);
    this.worldContainer.addChildAt(overlayTileLayer, bgLayerCount + 1);

    const offset = this.getGridOffset();

    // Render base tiles (hex-clipped) — all at alpha 0
    for (const [key, assignment] of Object.entries(tiles)) {
      const baseDef = BASE_TILES[assignment.baseTile as BaseTileId];
      if (!baseDef) continue;

      const texture = tileTextureCache.get(baseDef.path);
      if (!texture) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexGrid.toPixel(hex);

      const sprite = new PIXI.Sprite(texture);
      const imgSize = this.hexGrid.hexSize * 2.2;
      sprite.width = imgSize;
      sprite.height = imgSize;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x + offset.x;
      sprite.y = pixel.y + offset.y;
      sprite.alpha = 0; // Start invisible for crossfade

      // Hex polygon mask
      const mask = new PIXI.Graphics();
      mask.poly(this.getHexVertices(this.hexGrid.hexSize));
      mask.fill({ color: 0xffffff });
      mask.x = pixel.x + offset.x;
      mask.y = pixel.y + offset.y;

      sprite.mask = mask;
      baseTileLayer.addChild(mask);
      baseTileLayer.addChild(sprite);
      baseTileSprites.push(sprite);
    }

    // Collect and Y-sort overlays
    const overlayEntries: Array<{
      hex: AxialCoordinate;
      overlayId: OverlayTileId;
      pixelY: number;
    }> = [];

    for (const [key, assignment] of Object.entries(tiles)) {
      if (!assignment.overlay) continue;
      const overlayDef = OVERLAY_TILES[assignment.overlay as OverlayTileId];
      if (!overlayDef) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexGrid.toPixel(hex);
      overlayEntries.push({ hex, overlayId: assignment.overlay as OverlayTileId, pixelY: pixel.y });
    }

    overlayEntries.sort((a, b) => a.pixelY - b.pixelY);

    for (const { hex, overlayId } of overlayEntries) {
      const overlayDef = OVERLAY_TILES[overlayId];
      const texture = tileTextureCache.get(overlayDef.path);
      if (!texture) continue;

      const pixel = this.hexGrid.toPixel(hex);
      const sprite = new PIXI.Sprite(texture);
      const size = this.hexGrid.hexSize * 2 * overlayDef.scale;
      sprite.width = size;
      sprite.height = size;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x + offset.x;
      sprite.y = pixel.y + offset.y + overlayDef.offsetY;

      // Start invisible and slightly scaled down for pop-in effect
      sprite.alpha = 0;
      sprite.scale.x *= 0.8;
      sprite.scale.y *= 0.8;

      overlayTileLayer.addChild(sprite);
      overlaySprites.push(sprite);
    }

    this.constrainCameraToGrid();

    console.log(`[HexGridRenderer] Loaded ${Object.keys(tiles).length} tiles for crossfade, ${overlaySprites.length} overlays`);
    return { baseTileSprites, overlaySprites };
  }

  /**
   * Animate an array of sprites from their current alpha to 1 using easeOutCubic.
   */
  public fadeInSprites(sprites: PIXI.Sprite[], durationMs: number): void {
    if (sprites.length === 0 || durationMs <= 0) return;

    const startAlphas = sprites.map(s => s.alpha);
    const startTime = performance.now();

    const ticker = this.app.ticker;
    const onTick = () => {
      const elapsed = performance.now() - startTime;
      const rawT = Math.min(elapsed / durationMs, 1);
      const t = 1 - Math.pow(1 - rawT, 3); // easeOutCubic

      for (let i = 0; i < sprites.length; i++) {
        const sprite = sprites[i]!;
        if (sprite.destroyed) continue;
        sprite.alpha = startAlphas[i]! + (1 - startAlphas[i]!) * t;
      }

      if (rawT >= 1) {
        ticker.remove(onTick);
      }
    };

    ticker.add(onTick);
  }

  /**
   * Generic ticker-based tween with easeOutCubic for any PIXI.Container properties.
   */
  public animateProps(
    target: PIXI.Container,
    props: { alpha?: number; scaleX?: number; scaleY?: number },
    durationMs: number,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (durationMs <= 0 || target.destroyed) {
        if (props.alpha !== undefined) target.alpha = props.alpha;
        if (props.scaleX !== undefined) target.scale.x = props.scaleX;
        if (props.scaleY !== undefined) target.scale.y = props.scaleY;
        resolve();
        return;
      }

      const startAlpha = target.alpha;
      const startScaleX = target.scale.x;
      const startScaleY = target.scale.y;
      const startTime = performance.now();

      const ticker = this.app.ticker;
      const onTick = () => {
        if (target.destroyed) {
          ticker.remove(onTick);
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        const rawT = Math.min(elapsed / durationMs, 1);
        const t = 1 - Math.pow(1 - rawT, 3); // easeOutCubic

        if (props.alpha !== undefined) {
          target.alpha = startAlpha + (props.alpha - startAlpha) * t;
        }
        if (props.scaleX !== undefined) {
          target.scale.x = startScaleX + (props.scaleX - startScaleX) * t;
        }
        if (props.scaleY !== undefined) {
          target.scale.y = startScaleY + (props.scaleY - startScaleY) * t;
        }

        if (rawT >= 1) {
          ticker.remove(onTick);
          resolve();
        }
      };

      ticker.add(onTick);
    });
  }

  /**
   * Load a background image that persists behind hex tiles.
   * Zooms into the bottom portion of the image (hiding the horizon/sky)
   * and darkens it so tile hexes and creatures remain visually prominent.
   */
  protected async loadTileBackground(): Promise<void> {
    if (!this.tileBackgroundUrl) return;

    try {
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous';
      bgImg.src = this.tileBackgroundUrl;
      await new Promise<void>((resolve, reject) => {
        bgImg.onload = () => resolve();
        bgImg.onerror = () => reject(new Error(`Failed to load tile bg: ${this.tileBackgroundUrl}`));
      });

      const bgTexture = PIXI.Texture.from(bgImg);
      const bgSprite = new PIXI.Sprite(bgTexture);

      // Anchor near the bottom of the image to focus on the atmospheric scene
      // and push the sky/horizon off the top of the screen
      bgSprite.anchor.set(0.5, 0.65);

      // Position at grid center
      const gridBounds = this.getGridBounds();
      const gridOffset = this.getGridOffset();
      const gridCenterX = gridOffset.x + gridBounds.minX + gridBounds.width / 2;
      const gridCenterY = gridOffset.y + gridBounds.minY + gridBounds.height / 2;
      bgSprite.x = gridCenterX;
      bgSprite.y = gridCenterY;

      // Scale to cover the full canvas, focusing on the battlefield area
      const scaleX = this.config.canvasWidth / bgTexture.width;
      const scaleY = this.config.canvasHeight / (bgTexture.height * 0.6);
      const bgScale = Math.max(scaleX, scaleY) * 1.05;
      bgSprite.scale.set(bgScale);

      // Darken and desaturate so it doesn't compete with hex tiles and creatures
      const colorFilter = new PIXI.ColorMatrixFilter();
      colorFilter.reset();
      colorFilter.brightness(0.6, true);
      colorFilter.saturate(0.7, true);
      bgSprite.filters = [colorFilter];

      // Insert at the very back of worldContainer
      this.worldContainer.addChildAt(bgSprite, 0);

      console.log(`[HexGridRenderer] Tile background loaded: ${this.tileBackgroundUrl}`);
    } catch (error) {
      console.warn('[HexGridRenderer] Failed to load tile background:', error);
    }
  }

  /**
   * Constrain camera zoom and pan to the hex grid bounds.
   * - Sets minZoom so the full grid is visible at max zoom-out
   * - Sets bgWorldBounds so WASD/drag pan can't move the grid off-screen
   * - Centers the camera on the grid via fitToContent
   *
   * Call this after loading tiles, backgrounds, or any time the grid
   * dimensions or canvas size change. Works for any grid size.
   */
  protected constrainCameraToGrid(): void {
    if (!this.cameraManager) return;

    const offset = this.getGridOffset();
    const { width: cols, height: rows } = this.hexGrid.getConfig();
    const hexSize = this.hexGrid.hexSize;

    // Compute true pixel extent by scanning edge hex centers
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    for (let q = 0; q < cols; q++) {
      for (let r = 0; r < rows; r++) {
        if (q === 0 || q === cols - 1 || r === 0 || r === rows - 1) {
          const p = this.hexGrid.toPixel({ q, r });
          minX = Math.min(minX, p.x);
          maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y);
          maxY = Math.max(maxY, p.y);
        }
      }
    }

    // Full visual extent: center-to-center range + one hex radius on each side
    const worldBounds = {
      x: minX - hexSize + offset.x,
      y: minY - hexSize + offset.y,
      width: (maxX - minX) + hexSize * 2,
      height: (maxY - minY) + hexSize * 2,
    };

    // Min ensures BOTH dimensions fit in view
    const fitZoomX = this.config.canvasWidth / worldBounds.width;
    const fitZoomY = this.config.canvasHeight / worldBounds.height;
    const gridMinZoom = Math.min(fitZoomX, fitZoomY);

    this.cameraManager.updateConfig({
      minZoom: gridMinZoom,
      bgWorldBounds: {
        left: worldBounds.x,
        right: worldBounds.x + worldBounds.width,
        top: worldBounds.y,
        bottom: worldBounds.y + worldBounds.height,
      },
    });

    // Center camera on the grid
    this.cameraManager.fitToContent(
      worldBounds,
      this.config.canvasWidth,
      this.config.canvasHeight,
      0.01,
    );
    this.cameraManager.applyTransform();

    console.log(`[HexGridRenderer] constrainCameraToGrid: minZoom=${gridMinZoom.toFixed(3)}, world=${worldBounds.width.toFixed(0)}x${worldBounds.height.toFixed(0)}, canvas=${this.config.canvasWidth}x${this.config.canvasHeight}`);
  }

  /**
   * Calculate actual rendered bounding box of the grid
   * Accounts for isometric projection transformation
   */
  protected calculateGridBounds(): { width: number; height: number; minX: number; minY: number } {
    const { width, height } = this.hexGrid.getConfig();

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    // Sample all edge hexes to find true bounds after projection
    for (let q = 0; q < width; q++) {
      for (let r = 0; r < height; r++) {
        // Only check edge hexes for efficiency
        if (q === 0 || q === width - 1 || r === 0 || r === height - 1) {
          const pixel = this.hexGrid.toPixel({ q, r });
          minX = Math.min(minX, pixel.x);
          maxX = Math.max(maxX, pixel.x);
          minY = Math.min(minY, pixel.y);
          maxY = Math.max(maxY, pixel.y);
        }
      }
    }

    // Add hex size padding
    const padding = this.hexGrid.hexSize;

    return {
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
      minX: minX - padding,
      minY: minY - padding
    };
  }

  /**
   * Render the static hex grid
   */
  private renderStaticGrid(): void {
    const { width, height } = this.hexGrid.getConfig();
    const offset = this.getGridOffset();

    // Render all hexes
    for (let q = 0; q < width; q++) {
      for (let r = 0; r < height; r++) {
        const hex: AxialCoordinate = { q, r };
        const pixel = this.hexGrid.toPixel(hex);

        // Create hex graphic
        const hexGraphic = this.createHexGraphic(
          hex,
          { x: pixel.x + offset.x, y: pixel.y + offset.y }
        );

        this.gridContainer.addChild(hexGraphic);
        this.hexGraphics.set(this.hexGrid.hash(hex), hexGraphic);

        // Create coordinate text (initially hidden)
        if (this.showCoordinates) {
          const coordText = this.createCoordinateText(
            hex,
            { x: pixel.x + offset.x, y: pixel.y + offset.y }
          );
          this.coordinateLayer.addChild(coordText);
          this.coordinateTexts.set(this.hexGrid.hash(hex), coordText);
        }
      }
    }

    // Render deployment zones
    this.renderDeploymentZones(offset.x, offset.y);
  }

  /**
   * Create a single hex graphic
   */
  private createHexGraphic(_hex: AxialCoordinate, pixel: { x: number; y: number }): PIXI.Graphics {
    const graphic = new PIXI.Graphics();
    const vertices = this.getHexVertices(this.hexGrid.hexSize);

    // Draw hex outline
    graphic.stroke({ width: 2, color: 0x555555, alpha: 0.5 });
    graphic.fill({ color: 0x000000, alpha: 0 }); // Transparent fill
    graphic.poly(vertices);
    graphic.stroke();
    graphic.fill();

    // Position at pixel coordinate
    graphic.x = pixel.x;
    graphic.y = pixel.y;

    // Make interactive
    graphic.eventMode = 'static';
    graphic.cursor = 'pointer';

    return graphic;
  }

  /**
   * Get hex vertices for flat-top orientation
   * Applies isometric projection if enabled in config
   */
  protected getHexVertices(size: number): number[] {
    const angles = [0, 60, 120, 180, 240, 300]; // Degrees for flat-top
    const vertices: number[] = [];
    const isIsometric = this.config.hexGridConfig.projection === 'isometric';

    for (const angle of angles) {
      const rad = (Math.PI / 180) * angle;
      let x = size * Math.cos(rad);
      let y = size * Math.sin(rad);

      // Apply isometric projection to vertices if enabled
      if (isIsometric) {
        const x_iso = (x - y) * 0.5;
        const y_iso = (x + y) * 0.25;
        x = x_iso;
        y = y_iso;
      }

      vertices.push(x, y);
    }

    return vertices;
  }

  /**
   * Create coordinate text for debugging
   */
  private createCoordinateText(hex: AxialCoordinate, pixel: { x: number; y: number }): PIXI.Text {
    const text = new PIXI.Text({
      text: `${hex.q},${hex.r}`,
      style: {
        fontSize: 10,
        fill: 0xffffff,
        align: 'center'
      }
    });

    text.alpha = 0.4;
    text.anchor.set(0.5);
    text.x = pixel.x;
    text.y = pixel.y;

    return text;
  }

  /**
   * Render deployment zone overlays
   */
  private renderDeploymentZones(offsetX: number, offsetY: number): void {
    const { width, height } = this.hexGrid.getConfig();
    const zoneConfig = this.config.deploymentZoneConfig;

    // Skip zone rendering when no config is provided (e.g., combat renderer)
    if (!zoneConfig) return;

    const orientation = zoneConfig.orientation ?? 'left_vs_right';

    let zones: DeploymentZone[];

    if (orientation === 'top_vs_bottom') {
      // Top-vs-bottom row zones (co-op adventure: players at bottom, enemies at top)
      const playerRows = zoneConfig?.playerDeployRows ?? 2;
      const enemyRows = zoneConfig?.enemyDeployRows ?? 2;
      zones = [
        {
          playerId: 'player1',
          hexes: this.getRowZoneHexes(height - playerRows, height - 1, width), // Bottom N rows
          color: 0xd4a017, // Gold (co-op team color)
          alpha: 0.35
        },
        {
          playerId: 'player2',
          hexes: this.getRowZoneHexes(0, enemyRows - 1, width), // Top N rows
          color: 0x6a0dad, // Deep purple (enemy team color)
          alpha: 0.35
        }
      ];
    } else {
      // Left-vs-right column zones (PvP, challenge)
      const playerCols = zoneConfig?.playerDeployCols ?? 3;
      const p1Min = zoneConfig?.player1ColRange?.min ?? 0;
      const p1Max = zoneConfig?.player1ColRange?.max ?? (playerCols - 1);
      const p2Min = zoneConfig?.player2ColRange?.min ?? (width - 3);
      const p2Max = zoneConfig?.player2ColRange?.max ?? (width - 1);
      zones = [
        {
          playerId: 'player1',
          hexes: this.getColumnZoneHexes(p1Min, p1Max, height),
          color: 0x3498db, // Blue
          alpha: 0.35
        },
        {
          playerId: 'player2',
          hexes: this.getColumnZoneHexes(p2Min, p2Max, height),
          color: 0xe74c3c, // Red
          alpha: 0.35
        }
      ];
    }

    zones.forEach(zone => {
      const overlay = this.createZoneOverlay(zone, offsetX, offsetY);
      this.gridContainer.addChild(overlay);
      this.zoneOverlays.push(overlay);
    });
  }

  /**
   * Get all hexes in a column range (for left-vs-right zones)
   */
  private getColumnZoneHexes(minCol: number, maxCol: number, height: number): AxialCoordinate[] {
    const hexes: AxialCoordinate[] = [];
    for (let q = minCol; q <= maxCol; q++) {
      for (let r = 0; r < height; r++) {
        hexes.push({ q, r });
      }
    }
    return hexes;
  }

  /**
   * Get all hexes in a row range (for top-vs-bottom zones)
   */
  private getRowZoneHexes(minRow: number, maxRow: number, width: number): AxialCoordinate[] {
    const hexes: AxialCoordinate[] = [];
    for (let r = minRow; r <= maxRow; r++) {
      for (let q = 0; q < width; q++) {
        hexes.push({ q, r });
      }
    }
    return hexes;
  }

  /**
   * Create zone overlay graphic
   */
  private createZoneOverlay(zone: DeploymentZone, offsetX: number, offsetY: number): PIXI.Graphics {
    const graphic = new PIXI.Graphics();
    const vertices = this.getHexVertices(this.hexGrid.hexSize);

    zone.hexes.forEach(hex => {
      const pixel = this.hexGrid.toPixel(hex);
      const offsetVertices = vertices.map((v, i) =>
        i % 2 === 0 ? v + pixel.x + offsetX : v + pixel.y + offsetY
      );
      graphic.poly(offsetVertices);
      graphic.fill({ color: zone.color, alpha: zone.alpha });
      graphic.stroke({ color: zone.color, alpha: zone.alpha + 0.2, width: 1.5 });
    });

    return graphic;
  }

  /**
   * Remove deployment zone color overlays (call when transitioning to combat)
   */
  public clearDeploymentZones(): void {
    console.log('[HexGridRenderer] clearDeploymentZones: removing', this.zoneOverlays.length, 'overlays');
    for (const overlay of this.zoneOverlays) {
      overlay.destroy();
    }
    this.zoneOverlays = [];
  }

  /**
   * Setup mouse interaction
   */
  private setupInteraction(): void {
    this.app.stage.eventMode = 'static';
    this.app.stage.hitArea = this.app.screen;

    // Prevent browser default touch behaviors (text selection, scrolling)
    // This is critical for mobile tap-to-place functionality
    const canvas = this.app.canvas;
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.webkitUserSelect = 'none';

    // Track mouse movement for hover
    this.app.stage.on('pointermove', (event) => {
      const point = event.global;

      // Apply camera transforms to convert screen coordinates to world coordinates
      let worldX = point.x;
      let worldY = point.y;

      if (this.cameraManager) {
        const worldPos = this.cameraManager.screenToWorld({ x: point.x, y: point.y });
        worldX = worldPos.x;
        worldY = worldPos.y;
      }

      // Get cached grid offset
      const offset = this.getGridOffset();

      // Convert to hex coordinate
      const hex = this.hexGrid.fromPixel({
        x: worldX - offset.x,
        y: worldY - offset.y
      });

      // Check if valid hex
      if (this.hexGrid.isValid(hex)) {
        if (!this.currentHoverHex || !this.hexGrid.equals(this.currentHoverHex, hex)) {
          this.currentHoverHex = hex;
          this.updateHighlight(hex, HighlightState.HOVER);
          this.config.onHexHover?.(hex);
        }
      } else {
        if (this.currentHoverHex) {
          this.clearHighlights();
          this.currentHoverHex = null;
          this.config.onHexHover?.(null);
        }
      }
    });

    // Handle clicks/taps - calculate hex directly from event position
    // CRITICAL: Don't rely on currentHoverHex for mobile - tap doesn't trigger hover first
    this.app.stage.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      const point = event.global;

      // Apply camera transforms to convert screen coordinates to world coordinates
      let worldX = point.x;
      let worldY = point.y;

      if (this.cameraManager) {
        const worldPos = this.cameraManager.screenToWorld({ x: point.x, y: point.y });
        worldX = worldPos.x;
        worldY = worldPos.y;
      }

      // Get cached grid offset
      const offset = this.getGridOffset();

      // Convert to hex coordinate directly from tap/click position
      const hex = this.hexGrid.fromPixel({
        x: worldX - offset.x,
        y: worldY - offset.y
      });

      // Fire click if valid hex (don't require prior hover)
      if (this.hexGrid.isValid(hex)) {
        // Update hover state for visual feedback
        this.currentHoverHex = hex;
        this.config.onHexClick?.(hex);
      }
    });
  }

  /**
   * Update hex highlight
   */
  updateHighlight(hex: AxialCoordinate, state: HighlightState): void {
    // Clear existing highlights
    this.clearHighlights();

    // Create new highlight
    const highlight = this.createHighlight(hex, state);
    if (highlight) {
      const hash = this.hexGrid.hash(hex);
      this.highlightLayer.addChild(highlight);
      this.highlightGraphics.set(hash, highlight);
    }
  }

  /**
   * Create highlight graphic based on state
   */
  private createHighlight(hex: AxialCoordinate, state: HighlightState): PIXI.Graphics | null {
    if (state === HighlightState.NONE) return null;

    // Use cached grid offset for positioning
    const offset = this.getGridOffset();
    const pixel = this.hexGrid.toPixel(hex);
    const graphic = new PIXI.Graphics();
    const vertices = this.getHexVertices(this.hexGrid.hexSize);

    switch (state) {
      case HighlightState.HOVER:
        graphic.stroke({ width: 3, color: 0xf39c12, alpha: 1 }); // Yellow
        graphic.fill({ color: 0xf39c12, alpha: 0.3 });
        break;

      case HighlightState.VALID:
        graphic.stroke({ width: 3, color: 0x2ecc71, alpha: 1 }); // Green
        graphic.fill({ color: 0x2ecc71, alpha: 0.4 });
        break;

      case HighlightState.INVALID:
        graphic.stroke({ width: 3, color: 0xe74c3c, alpha: 1 }); // Red
        graphic.fill({ color: 0xe74c3c, alpha: 0.4 });
        break;

      case HighlightState.SELECTED:
        graphic.stroke({ width: 4, color: 0xecf0f1, alpha: 1 }); // White
        graphic.fill({ color: 0xecf0f1, alpha: 0.2 });
        break;

      case HighlightState.RANGE:
        graphic.stroke({ width: 2, color: 0x3498db, alpha: 1 }); // Blue
        graphic.fill({ color: 0x3498db, alpha: 0.2 });
        break;
    }

    graphic.poly(vertices);
    graphic.stroke();
    graphic.fill();
    graphic.x = pixel.x + offset.x;
    graphic.y = pixel.y + offset.y;

    return graphic;
  }

  /**
   * Clear all highlights
   */
  clearHighlights(): void {
    this.highlightLayer.removeChildren();
    this.highlightGraphics.clear();
  }

  /**
   * Toggle coordinate display
   */
  toggleCoordinates(): void {
    this.showCoordinates = !this.showCoordinates;
    this.coordinateLayer.visible = this.showCoordinates;

    // Create coordinate texts if they don't exist
    if (this.showCoordinates && this.coordinateTexts.size === 0) {
      const { width, height } = this.hexGrid.getConfig();
      const offset = this.getGridOffset();

      for (let q = 0; q < width; q++) {
        for (let r = 0; r < height; r++) {
          const hex: AxialCoordinate = { q, r };
          const pixel = this.hexGrid.toPixel(hex);
          const coordText = this.createCoordinateText(
            hex,
            { x: pixel.x + offset.x, y: pixel.y + offset.y }
          );
          this.coordinateLayer.addChild(coordText);
          this.coordinateTexts.set(this.hexGrid.hash(hex), coordText);
        }
      }
    }
  }

  /**
   * Render a creature sprite on a hex
   * @param hex - Hex coordinate to render at
   * @param creatureName - Name of the creature (used for fallback)
   * @param playerId - Player ID for color theming
   * @param spriteData - Sprite image URL (base64 or HTTP) OR DeploymentCreature object with battlefield directional views
   * @param opacity - Opacity for the sprite (0-1)
   * @param direction - Facing direction for multi-directional sprites (defaults to E)
   * @param creatureId - Unique creature ID for click handling (optional)
   * @param sizeMultiplier - Size multiplier for creature (0.3-3.0, default 1.0)
   */
  renderCreature(
    hex: AxialCoordinate,
    creatureName: string,
    playerId: 'player1' | 'player2',
    spriteData?: string | import('@drawn-of-war/shared').DeploymentCreature | {
      spriteImageBase64?: string;
      battlefieldSprite?: string;
      battlefieldDirectionalViews?: import('@drawn-of-war/shared').BattlefieldDirectionalViews;
      size?: number;
    },
    opacity: number = 1.0,
    direction: number = 1, // HexDirection.E = 1 (East is default)
    creatureId?: string,
    sizeMultiplier?: number
  ): void {
    const hash = this.hexGrid.hash(hex);

    // Remove existing sprite if any
    const existingSprite = this.creatureSprites.get(hash);
    if (existingSprite) {
      this.creatureLayer.removeChild(existingSprite);
    }

    // Use cached grid offset for positioning
    const offset = this.getGridOffset();
    const pixel = this.hexGrid.toPixel(hex);

    console.log(`[${this.constructor.name}] renderCreature called for ${creatureName}`);
    console.log(`[${this.constructor.name}] spriteData type:`, typeof spriteData);
    console.log(`[${this.constructor.name}] spriteData:`, spriteData);
    console.log(`[${this.constructor.name}] direction:`, direction);

    // Extract sprite URL and animation frames with directional support
    let spriteUrl: string | undefined;
    let idleFrames: string[] | undefined;
    let walkFrames: string[] | undefined;
    let shouldMirror = false;
    let extractedSize: number | undefined;

    if (typeof spriteData === 'string') {
      // Legacy: direct base64 string
      spriteUrl = spriteData;
      console.log(`[${this.constructor.name}] Legacy mode - direct sprite URL`);
    } else if (spriteData && typeof spriteData === 'object') {
      // NEW: Multi-directional support
      console.log(`[${this.constructor.name}] Object mode - checking for battlefieldDirectionalViews`);
      console.log(`[${this.constructor.name}] Has battlefieldDirectionalViews:`, !!spriteData.battlefieldDirectionalViews);

      if (spriteData.battlefieldDirectionalViews) {
        const dirViews = spriteData.battlefieldDirectionalViews;
        console.log(`[${this.constructor.name}] Directional views:`, dirViews);

        // Map direction to sprite and animation frames (with mirroring for W, NW, SW)
        switch (direction) {
          case 1: // E (East)
            spriteUrl = dirViews.E.sprite;
            idleFrames = dirViews.E.idleFrames;
            walkFrames = dirViews.E.walkFrames;
            shouldMirror = false;
            console.log(`[${this.constructor.name}] Direction E - idleFrames:`, idleFrames);
            console.log(`[${this.constructor.name}] Direction E - idleFrames length:`, idleFrames?.length);
            break;
          case 0: // NE (Northeast)
            spriteUrl = dirViews.NE.sprite;
            idleFrames = dirViews.NE.idleFrames;
            walkFrames = dirViews.NE.walkFrames;
            shouldMirror = false;
            break;
          case 2: // SE (Southeast)
            spriteUrl = dirViews.SE.sprite;
            idleFrames = dirViews.SE.idleFrames;
            walkFrames = dirViews.SE.walkFrames;
            shouldMirror = false;
            break;
          case 4: // W (West) - mirror of E
            spriteUrl = dirViews.E.sprite;
            idleFrames = dirViews.E.idleFrames;
            walkFrames = dirViews.E.walkFrames;
            shouldMirror = true;
            break;
          case 5: // NW (Northwest) - mirror of NE
            spriteUrl = dirViews.NE.sprite;
            idleFrames = dirViews.NE.idleFrames;
            walkFrames = dirViews.NE.walkFrames;
            shouldMirror = true;
            break;
          case 3: // SW (Southwest) - mirror of SE
            spriteUrl = dirViews.SE.sprite;
            idleFrames = dirViews.SE.idleFrames;
            walkFrames = dirViews.SE.walkFrames;
            shouldMirror = true;
            break;
          default:
            // Fallback to E if unknown direction
            spriteUrl = dirViews.E.sprite;
            idleFrames = dirViews.E.idleFrames;
            walkFrames = dirViews.E.walkFrames;
            shouldMirror = false;
        }
      } else {
        // Fallback to legacy battlefield sprite, menu sprite, or basic sprite field
        spriteUrl = (spriteData as any).battlefieldSprite || (spriteData as any).spriteImageBase64 || (spriteData as any).sprite;
      }

      // Extract size from spriteData if available
      extractedSize = (spriteData as any).size;
    }

    // Compute effective size multiplier: parameter > extracted from data > default 1.0
    const effectiveSizeMultiplier = sizeMultiplier ?? extractedSize ?? 1.0;

    console.log(`[${this.constructor.name}] After extraction - idleFrames:`, idleFrames);
    console.log(`[${this.constructor.name}] After extraction - walkFrames:`, walkFrames);
    console.log(`[${this.constructor.name}] After extraction - spriteUrl:`, spriteUrl);
    console.log(`[${this.constructor.name}] After extraction - size:`, effectiveSizeMultiplier);

    // Create sprite container
    const spriteContainer = new PIXI.Container();
    spriteContainer.alpha = opacity; // Set opacity for entire container

    if (spriteUrl || idleFrames || walkFrames) {
      // Load and render actual sprite image or animation
      console.log(`[${this.constructor.name}] Calling loadAndRenderSprite with idleFrames:`, idleFrames?.length ?? 0, 'size:', effectiveSizeMultiplier);
      this.loadAndRenderSprite(spriteContainer, spriteUrl, playerId, creatureName, shouldMirror, idleFrames, walkFrames, effectiveSizeMultiplier);
    } else {
      // Fallback: Create circular background with text - also scaled
      const bg = new PIXI.Graphics();
      const bgColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
      bg.circle(0, 0, this.hexGrid.hexSize * 0.6 * effectiveSizeMultiplier);
      bg.fill({ color: bgColor, alpha: 0.8 });
      spriteContainer.addChild(bg);

      // Create text label (placeholder)
      const text = new PIXI.Text({
        text: creatureName.charAt(0).toUpperCase(),
        style: {
          fontSize: 24,
          fill: 0xffffff,
          fontWeight: 'bold',
          align: 'center'
        }
      });
      text.anchor.set(0.5);
      spriteContainer.addChild(text);
    }

    // Position sprite
    spriteContainer.x = pixel.x + offset.x;
    spriteContainer.y = pixel.y + offset.y;

    // Add to layer and cache
    this.creatureLayer.addChild(spriteContainer);
    this.creatureSprites.set(hash, spriteContainer);

    // Store creature metadata for interactivity
    if (creatureId) {
      this.creatureMetadata.set(hash, {
        creatureId,
        playerId,
        hex
      });

      // Make sprite interactive if it belongs to the current player
      if (this.config.currentPlayerId && playerId === this.config.currentPlayerId && this.config.onCreatureClick) {
        this.setupCreatureInteractivity(spriteContainer, creatureId, hex, playerId);
      }
    }
  }

  /**
   * Setup interactivity for a creature sprite (click and hover effects)
   */
  private setupCreatureInteractivity(
    container: PIXI.Container,
    creatureId: string,
    hex: AxialCoordinate,
    playerId: 'player1' | 'player2'
  ): void {
    // Make container interactive
    container.eventMode = 'static';
    container.cursor = 'grab';

    // Create highlight ring for hover effect
    const highlightRing = new PIXI.Graphics();
    const ringColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
    highlightRing.circle(0, 0, this.hexGrid.hexSize * 0.75);
    highlightRing.stroke({ width: 3, color: ringColor, alpha: 0.8 });
    highlightRing.visible = false;
    highlightRing.name = 'hoverHighlight';
    container.addChildAt(highlightRing, 0); // Add at bottom so it's behind sprite

    // Hover effects
    container.on('pointerover', () => {
      container.alpha = Math.min(container.alpha + 0.2, 1.0); // Brighten slightly
      highlightRing.visible = true;
    });

    container.on('pointerout', () => {
      // Restore original alpha (accounting for opponent opacity)
      const metadata = this.creatureMetadata.get(this.hexGrid.hash(hex));
      if (metadata && metadata.playerId === this.config.currentPlayerId) {
        container.alpha = 1.0; // Own creatures are full opacity
      }
      highlightRing.visible = false;
    });

    // Unified pointer down handler
    container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();

      // Right-click: immediate removal (desktop)
      if (event.button === 2) {
        event.preventDefault();
        this.config.onCreatureRightClick?.(creatureId, hex);
        return;
      }

      // Left-click/touch: start gesture tracking if controller available
      if (event.button === 0 && this.removalGestureController) {
        this.removalGestureController.startTracking(
          creatureId,
          hex,
          { x: event.globalX, y: event.globalY }
        );
      } else if (event.button === 0) {
        // No gesture controller, immediate click handler
        this.config.onCreatureClick?.(creatureId, hex);
      }
    });

    // Track pointer movement for gesture (cancel if moved too far)
    container.on('pointermove', (event: PIXI.FederatedPointerEvent) => {
      if (this.removalGestureController) {
        this.removalGestureController.updatePointer({
          x: event.globalX,
          y: event.globalY
        });
      }
    });

    // End gesture on pointer up
    container.on('pointerup', () => {
      if (this.removalGestureController) {
        const state = this.removalGestureController.getState();

        if (state.phase === 'detecting') {
          // Quick tap - trigger normal click behavior for reposition
          this.removalGestureController.endTracking();
          this.config.onCreatureClick?.(creatureId, hex);
        } else {
          // Was in removing phase - just end tracking (will cancel if incomplete)
          this.removalGestureController.endTracking();
        }
      }
    });

    // Cancel on pointer leave
    container.on('pointerupoutside', () => {
      this.removalGestureController?.cancel();
    });

    container.on('pointercancel', () => {
      this.removalGestureController?.cancel();
    });

    // Right-click handler for removal (kept for explicit right-click handling)
    container.on('rightdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      event.preventDefault(); // Prevent browser context menu
      this.config.onCreatureRightClick?.(creatureId, hex);
    });
  }

  /**
   * Load and render a sprite image or animation into a container
   * @param shouldMirror - Whether to horizontally mirror the sprite
   * @param idleFrames - Optional array of idle animation frame URLs
   * @param walkFrames - Optional array of walk animation frame URLs
   * @param sizeMultiplier - Size multiplier for creature (0.3-3.0, default 1.0)
   */
  private async loadAndRenderSprite(
    container: PIXI.Container,
    spriteUrl: string | undefined,
    playerId: 'player1' | 'player2',
    creatureName: string,
    shouldMirror: boolean = false,
    idleFrames?: string[],
    walkFrames?: string[],
    sizeMultiplier: number = 1.0
  ): Promise<void> {
    try {
      // PRIORITY 1: Use idle animation frames if available
      if (idleFrames && idleFrames.length > 0) {
        console.log(`[${this.constructor.name}] Loading idle animation with ${idleFrames.length} frames for ${creatureName}`);

        // DEBUG: Log exact type and content of each frame
        idleFrames.forEach((frame, idx) => {
          console.log(`[${this.constructor.name}] Frame ${idx} type: ${typeof frame}, isString: ${typeof frame === 'string'}, value preview:`,
            typeof frame === 'string' ? frame.substring(0, 50) : frame);
        });

        // Load all frame textures in parallel
        // Normalize URLs to handle raw base64 data (challenge creatures have inline sprite data)
        // CRITICAL: Use async callback to properly wait for texture loading
        const textures = await Promise.all(
          idleFrames.map(async (url, idx) => {
            // Safety check: ensure url is a string
            if (typeof url !== 'string') {
              console.error(`[${this.constructor.name}] Frame ${idx} is NOT a string! Type: ${typeof url}`, url);
              // Try to extract string if it's an object with a url/data property
              if (url && typeof url === 'object') {
                const possibleKeys = ['url', 'data', 'src', 'base64', 'imageBase64'];
                for (const key of possibleKeys) {
                  if (typeof (url as any)[key] === 'string') {
                    console.log(`[${this.constructor.name}] Found string in object.${key}`);
                    url = (url as any)[key];
                    break;
                  }
                }
              }
              // If still not a string, skip this frame
              if (typeof url !== 'string') {
                console.error(`[${this.constructor.name}] Could not extract string from frame ${idx}, returning empty texture`);
                return PIXI.Texture.EMPTY;
              }
            }

            const normalizedUrl = this.normalizeImageUrl(url);
            console.log(`[${this.constructor.name}] Frame ${idx} normalized, startsWithData: ${normalizedUrl.startsWith('data:')}, length: ${normalizedUrl.length}`);

            // For ALL URLs (including data URIs), use Assets.load to properly wait for texture loading
            // PIXI.Texture.from() returns immediately but the image may not be decoded yet
            try {
              // Generate a unique asset key for data URIs to avoid caching issues
              const assetKey = normalizedUrl.startsWith('data:')
                ? `data-uri-${creatureName}-${idx}-${Date.now()}`
                : normalizedUrl;

              // Register the asset if it's a data URI (Assets.load needs the asset to be registered)
              if (normalizedUrl.startsWith('data:') && !PIXI.Assets.cache.has(assetKey)) {
                PIXI.Assets.add({ alias: assetKey, src: normalizedUrl });
              }

              const texture = await PIXI.Assets.load(assetKey);
              if (texture && texture.width && texture.height) {
                console.log(`[${this.constructor.name}] Frame ${idx} loaded via Assets.load: ${texture.width}x${texture.height}`);
                return texture;
              }
              console.warn(`[${this.constructor.name}] Frame ${idx} Assets.load returned invalid texture, trying fallback`);
            } catch (loadError) {
              console.warn(`[${this.constructor.name}] Frame ${idx} Assets.load failed:`, loadError);
            }

            // Fallback for data URIs: use Texture.from (PIXI.js v8 loads synchronously for data URIs)
            if (normalizedUrl.startsWith('data:')) {
              try {
                const texture = PIXI.Texture.from(normalizedUrl);
                // In PIXI.js v8, Texture.from with data URI should be synchronous
                // Small delay to ensure texture source is processed
                await new Promise(resolve => setTimeout(resolve, 10));
                if (texture.width && texture.height) {
                  console.log(`[${this.constructor.name}] Frame ${idx} loaded via Texture.from: ${texture.width}x${texture.height}`);
                  return texture;
                }
                // If still no dimensions, texture creation failed
                console.error(`[${this.constructor.name}] Frame ${idx} Texture.from created texture with invalid dimensions`);
                return PIXI.Texture.EMPTY;
              } catch (fallbackError) {
                console.error(`[${this.constructor.name}] Frame ${idx} Texture.from fallback failed:`, fallbackError);
                return PIXI.Texture.EMPTY;
              }
            }

            // Fallback for HTTP URLs: fetch and convert to data URI
            try {
              const response = await fetch(normalizedUrl);
              if (!response.ok) {
                console.error(`[${this.constructor.name}] Frame ${idx} fetch failed: ${response.status}`);
                return PIXI.Texture.EMPTY;
              }
              const blob = await response.blob();
              const dataUri = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              const texture = PIXI.Texture.from(dataUri);
              // Small delay for PIXI.js v8 to process the texture
              await new Promise(resolve => setTimeout(resolve, 10));
              console.log(`[${this.constructor.name}] Frame ${idx} loaded via fetch fallback: ${texture.width}x${texture.height}`);
              return texture;
            } catch (fetchError) {
              console.error(`[${this.constructor.name}] Frame ${idx} fetch fallback also failed:`, fetchError);
              return PIXI.Texture.EMPTY;
            }
          })
        );

        // VALIDATION: Filter out invalid textures and check for valid frames
        const validTextures = textures.filter((tex, idx) => {
          if (!tex) {
            console.error(`[${this.constructor.name}] Frame ${idx} texture is null/undefined`);
            return false;
          }
          if (tex === PIXI.Texture.EMPTY) {
            console.error(`[${this.constructor.name}] Frame ${idx} texture is EMPTY`);
            return false;
          }
          if (!tex.width || !tex.height) {
            console.error(`[${this.constructor.name}] Frame ${idx} texture has invalid dimensions: ${tex.width}x${tex.height}`);
            return false;
          }
          return true;
        });

        if (validTextures.length === 0) {
          console.error(`[${this.constructor.name}] No valid textures loaded for ${creatureName}! Falling back to static sprite.`);
          // Fall through to static sprite fallback below
        } else {
          console.log(`[${this.constructor.name}] ${validTextures.length}/${textures.length} valid textures for ${creatureName}`);

          // Create animated sprite with validated textures
          const animatedSprite = new PIXI.AnimatedSprite(validTextures);

          // Configure animation
          animatedSprite.animationSpeed = 0.08; // Slow speed for subtle idle breathing (about 1-2 FPS)
          animatedSprite.play(); // Start animation loop
          animatedSprite.loop = true; // Ensure looping

          // Scale sprite to fit hex (with some padding) - apply size multiplier
          const targetSize = this.hexGrid.hexSize * 1.2 * sizeMultiplier;
          const baseScale = Math.min(
            targetSize / validTextures[0].width,
            targetSize / validTextures[0].height
          );

          // Apply mirroring if needed
          if (shouldMirror) {
            animatedSprite.scale.set(-baseScale, baseScale); // Negative X scale for horizontal flip
          } else {
            animatedSprite.scale.set(baseScale, baseScale);
          }

          // Center sprite
          animatedSprite.anchor.set(0.5);

          // Add subtle background glow for team color - also scaled
          const glow = new PIXI.Graphics();
          const glowColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
          glow.circle(0, 0, this.hexGrid.hexSize * 0.65 * sizeMultiplier);
          glow.fill({ color: glowColor, alpha: 0.2 });
          container.addChild(glow);

          // Add animated sprite
          container.addChild(animatedSprite);

          console.log(`[${this.constructor.name}] Idle animation loaded and playing for ${creatureName}`);
          return;
        }
      }

      // PRIORITY 2: Fallback to static sprite if available
      if (spriteUrl) {
        // Normalize URL to handle raw base64 data (challenge creatures have inline sprite data)
        const normalizedSpriteUrl = this.normalizeImageUrl(spriteUrl);
        console.log(`[${this.constructor.name}] Loading static sprite for ${creatureName}, length: ${normalizedSpriteUrl.length}`);

        let texture: PIXI.Texture | null = null;

        // Try Assets.load first for all URL types (properly waits for loading)
        try {
          const assetKey = normalizedSpriteUrl.startsWith('data:')
            ? `static-sprite-${creatureName}-${Date.now()}`
            : normalizedSpriteUrl;

          if (normalizedSpriteUrl.startsWith('data:') && !PIXI.Assets.cache.has(assetKey)) {
            PIXI.Assets.add({ alias: assetKey, src: normalizedSpriteUrl });
          }

          texture = await PIXI.Assets.load(assetKey);
          if (texture && texture.width && texture.height) {
            console.log(`[${this.constructor.name}] Static sprite loaded via Assets.load: ${texture.width}x${texture.height}`);
          } else {
            console.warn(`[${this.constructor.name}] Static sprite Assets.load returned invalid texture`);
            texture = null;
          }
        } catch (loadError) {
          console.warn(`[${this.constructor.name}] Static sprite Assets.load failed:`, loadError);
        }

        // Fallback for data URIs: use Texture.from (PIXI.js v8)
        if (!texture && normalizedSpriteUrl.startsWith('data:')) {
          try {
            texture = PIXI.Texture.from(normalizedSpriteUrl);
            // Small delay for PIXI.js v8 to process the texture
            await new Promise(resolve => setTimeout(resolve, 10));
            if (texture.width && texture.height) {
              console.log(`[${this.constructor.name}] Static sprite loaded via Texture.from: ${texture.width}x${texture.height}`);
            } else {
              console.error(`[${this.constructor.name}] Static sprite Texture.from created invalid texture`);
              texture = null;
            }
          } catch (fallbackError) {
            console.error(`[${this.constructor.name}] Static sprite Texture.from failed:`, fallbackError);
            texture = null;
          }
        }

        // Fallback for HTTP URLs: fetch and convert to data URI
        if (!texture && !normalizedSpriteUrl.startsWith('data:')) {
          console.warn(`[${this.constructor.name}] Static sprite trying fetch fallback`);
          try {
            const response = await fetch(normalizedSpriteUrl);
            if (response.ok) {
              const blob = await response.blob();
              const dataUri = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              texture = PIXI.Texture.from(dataUri);
              // Small delay for PIXI.js v8 to process the texture
              await new Promise(resolve => setTimeout(resolve, 10));
              console.log(`[${this.constructor.name}] Static sprite loaded via fetch: ${texture.width}x${texture.height}`);
            }
          } catch (fetchError) {
            console.error(`[${this.constructor.name}] Static sprite fetch fallback also failed:`, fetchError);
          }
        }

        // Check if texture loaded successfully
        if (!texture) {
          throw new Error(`Failed to load texture from URL: ${spriteUrl}`);
        }

        // Validate texture has required properties
        if (!texture.width || !texture.height) {
          throw new Error(`Invalid texture dimensions: ${texture.width}x${texture.height}`);
        }

        // Create sprite from texture
        const sprite = new PIXI.Sprite(texture);

        // Scale sprite to fit hex (with some padding) - apply size multiplier
        const targetSize = this.hexGrid.hexSize * 1.2 * sizeMultiplier;
        const baseScale = Math.min(
          targetSize / texture.width,
          targetSize / texture.height
        );

        // Apply mirroring if needed
        if (shouldMirror) {
          sprite.scale.set(-baseScale, baseScale); // Negative X scale for horizontal flip
        } else {
          sprite.scale.set(baseScale, baseScale);
        }

        // Center sprite
        sprite.anchor.set(0.5);

        // Add subtle background glow for team color - also scaled
        const glow = new PIXI.Graphics();
        const glowColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
        glow.circle(0, 0, this.hexGrid.hexSize * 0.65 * sizeMultiplier);
        glow.fill({ color: glowColor, alpha: 0.2 });
        container.addChild(glow);

        // Add sprite
        container.addChild(sprite);
        return;
      }

      // PRIORITY 3: No sprite data available
      throw new Error('No sprite data available (neither idleFrames nor spriteUrl)');
    } catch (error) {
      console.error(`[${this.constructor.name}] Failed to load sprite:`, error);

      // Fallback to placeholder on error
      const bg = new PIXI.Graphics();
      const bgColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
      bg.circle(0, 0, this.hexGrid.hexSize * 0.6);
      bg.fill({ color: bgColor, alpha: 0.8 });
      container.addChild(bg);

      const text = new PIXI.Text({
        text: creatureName.charAt(0).toUpperCase(),
        style: {
          fontSize: 24,
          fill: 0xffffff,
          fontWeight: 'bold',
          align: 'center'
        }
      });
      text.anchor.set(0.5);
      container.addChild(text);
    }
  }

  /**
   * Remove creature sprite from a hex
   */
  removeCreature(hex: AxialCoordinate): void {
    const hash = this.hexGrid.hash(hex);
    const sprite = this.creatureSprites.get(hash);

    if (sprite) {
      this.creatureLayer.removeChild(sprite);
      this.creatureSprites.delete(hash);
      this.creatureMetadata.delete(hash);
    }
  }

  /**
   * Clear all creature sprites
   */
  clearAllCreatures(): void {
    this.creatureLayer.removeChildren();
    this.creatureSprites.clear();
    this.creatureMetadata.clear();
  }

  /**
   * Render a ghost/preview of a creature being dragged
   */
  renderDragPreview(
    hex: AxialCoordinate,
    creatureName: string,
    playerId: 'player1' | 'player2',
    spriteData?: string | import('@drawn-of-war/shared').DeploymentCreature | {
      spriteImageBase64?: string;
      battlefieldSprite?: string;
      battlefieldDirectionalViews?: import('@drawn-of-war/shared').BattlefieldDirectionalViews;
    },
    direction: number = 1 // HexDirection.E = 1 (East is default)
  ): void {
    // First clear any existing preview
    this.clearDragPreview();

    const hash = 'drag-preview';

    // Use cached grid offset for positioning
    const offset = this.getGridOffset();
    const pixel = this.hexGrid.toPixel(hex);

    // Extract sprite URL with directional support
    let spriteUrl: string | undefined;
    let shouldMirror = false;

    if (typeof spriteData === 'string') {
      // Legacy: direct base64 string
      spriteUrl = spriteData;
    } else if (spriteData && typeof spriteData === 'object') {
      // NEW: Multi-directional support
      if (spriteData.battlefieldDirectionalViews) {
        const dirViews = spriteData.battlefieldDirectionalViews;

        // Map direction to sprite (with mirroring for W, NW, SW)
        switch (direction) {
          case 1: // E (East)
            spriteUrl = dirViews.E.sprite;
            shouldMirror = false;
            break;
          case 0: // NE (Northeast)
            spriteUrl = dirViews.NE.sprite;
            shouldMirror = false;
            break;
          case 2: // SE (Southeast)
            spriteUrl = dirViews.SE.sprite;
            shouldMirror = false;
            break;
          case 4: // W (West) - mirror of E
            spriteUrl = dirViews.E.sprite;
            shouldMirror = true;
            break;
          case 5: // NW (Northwest) - mirror of NE
            spriteUrl = dirViews.NE.sprite;
            shouldMirror = true;
            break;
          case 3: // SW (Southwest) - mirror of SE
            spriteUrl = dirViews.SE.sprite;
            shouldMirror = true;
            break;
          default:
            // Fallback to E if unknown direction
            spriteUrl = dirViews.E.sprite;
            shouldMirror = false;
        }
      } else {
        // Fallback to legacy battlefield sprite, menu sprite, or basic sprite field
        spriteUrl = (spriteData as any).battlefieldSprite || (spriteData as any).spriteImageBase64 || (spriteData as any).sprite;
      }
    }

    // Create sprite container
    const spriteContainer = new PIXI.Container();
    spriteContainer.alpha = 0.5; // Ghost effect

    if (spriteUrl) {
      // Load and render actual sprite image (with ghost effect)
      this.loadAndRenderSprite(spriteContainer, spriteUrl, playerId, creatureName, shouldMirror);
    } else {
      // Fallback: Create circular background
      const bg = new PIXI.Graphics();
      const bgColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
      bg.circle(0, 0, this.hexGrid.hexSize * 0.6);
      bg.fill({ color: bgColor, alpha: 0.6 });
      spriteContainer.addChild(bg);

      // Create text label
      const text = new PIXI.Text({
        text: creatureName.charAt(0).toUpperCase(),
        style: {
          fontSize: 24,
          fill: 0xffffff,
          fontWeight: 'bold',
          align: 'center'
        }
      });
      text.anchor.set(0.5);
      spriteContainer.addChild(text);
    }

    // Position sprite
    spriteContainer.x = pixel.x + offset.x;
    spriteContainer.y = pixel.y + offset.y;

    // Add to layer and cache
    this.creatureLayer.addChild(spriteContainer);
    this.creatureSprites.set(hash, spriteContainer);
  }

  /**
   * Clear drag preview
   */
  clearDragPreview(): void {
    const preview = this.creatureSprites.get('drag-preview');
    if (preview) {
      this.creatureLayer.removeChild(preview);
      this.creatureSprites.delete('drag-preview');
    }
  }

  /**
   * Destroy the renderer and cleanup resources
   */
  destroy(): void {
    // Mark as destroyed to prevent race conditions with async init
    this.isDestroyed = true;

    // Destroy terrain renderer
    if (this.terrainRenderer) {
      this.terrainRenderer.destroy();
      this.terrainRenderer = null;
    }

    // Destroy removal controllers
    if (this.removalAnimationController) {
      this.removalAnimationController.destroy();
      this.removalAnimationController = null;
    }

    if (this.removalGestureController) {
      this.removalGestureController.destroy();
      this.removalGestureController = null;
    }

    // Only destroy PixiJS app if it was fully initialized
    if (this.isInitialized && this.app.stage) {
      this.hexGraphics.clear();
      this.highlightGraphics.clear();
      this.coordinateTexts.clear();
      this.creatureSprites.clear();
      this.creatureMetadata.clear();
      // Don't destroy textures - they're managed by PIXI.Assets and shared with combat
      this.app.destroy(true, { children: true, texture: false });
    }
    // If init() was called but not completed, it will handle cleanup
  }

  /**
   * Get the PixiJS canvas element
   */
  getCanvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  /**
   * Resize the canvas and update camera configuration.
   * Used for responsive layouts (e.g., orientation changes on mobile).
   */
  resize(width: number, height: number): void {
    if (this.isDestroyed) return;

    // Update internal config
    this.config.canvasWidth = width;
    this.config.canvasHeight = height;

    // Resize PixiJS renderer
    this.app.renderer.resize(width, height);

    // Invalidate grid offset cache (depends on canvas dimensions)
    this.invalidateGridOffset();

    // Update camera manager config
    if (this.cameraManager) {
      this.cameraManager.updateConfig({
        canvasWidth: width,
        canvasHeight: height,
      });
    }
  }

  /**
   * Get the hex grid instance
   */
  getHexGrid(): HexGrid {
    return this.hexGrid;
  }

  /**
   * Get the PixiJS stage container
   * Used by effect renderers (CombatVisualizationManager) to add visual effect layers
   */
  getStage(): PIXI.Container {
    return this.app.stage;
  }

  /**
   * Get hex coordinate at pixel position (relative to canvas)
   * Used for HTML5 drag-and-drop hit detection
   * Accounts for camera zoom/pan transforms
   */
  getHexAtPixel(x: number, y: number): AxialCoordinate | null {
    // Transform screen coordinates to world coordinates if camera is active
    let worldX = x;
    let worldY = y;

    if (this.cameraManager) {
      const worldPos = this.cameraManager.screenToWorld({ x, y });
      worldX = worldPos.x;
      worldY = worldPos.y;
    }

    // Use cached grid offset
    const offset = this.getGridOffset();

    // Convert pixel coordinates to hex coordinates
    const hex = this.hexGrid.fromPixel({
      x: worldX - offset.x,
      y: worldY - offset.y
    });

    // Validate hex is within grid bounds
    if (this.hexGrid.isValid(hex)) {
      return hex;
    }

    return null;
  }

  /**
   * Set the battle phase (triggers terrain effects)
   */
  public setBattlePhase(phase: BattlePhase): void {
    if (this.terrainRenderer) {
      this.terrainRenderer.setPhase(phase);
    }
  }

  /**
   * Notify terrain renderer of hex hover
   */
  public notifyHexHover(hex: AxialCoordinate | null): void {
    if (this.terrainRenderer) {
      this.terrainRenderer.onHexHover(hex);
    }
  }

  /**
   * Notify terrain renderer of creature spawn
   */
  public notifyCreatureSpawned(unitId: string, hex: AxialCoordinate): void {
    if (this.terrainRenderer) {
      this.terrainRenderer.onCreatureSpawned(unitId, hex);
    }
  }

  /**
   * Calculate grid bounds for terrain renderer
   */
  protected calculateGridBoundsForTerrain(): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    const { width, height } = this.hexGrid.getConfig();

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    // Sample all hexes to find bounds
    for (let q = 0; q < width; q++) {
      for (let r = 0; r < height; r++) {
        const pixel = this.hexToPixel({ q, r });
        minX = Math.min(minX, pixel.x);
        maxX = Math.max(maxX, pixel.x);
        minY = Math.min(minY, pixel.y);
        maxY = Math.max(maxY, pixel.y);
      }
    }

    // Add padding for particle spawn areas
    const padding = this.hexGrid.hexSize * 2;

    return {
      minX: minX - padding,
      maxX: maxX + padding,
      minY: minY - padding,
      maxY: maxY + padding
    };
  }

  /**
   * Convert hex coordinates to screen pixel coordinates
   * Includes proper grid centering and offset calculations
   *
   * @param hex - Hex coordinate to convert
   * @returns Pixel coordinates {x, y} relative to canvas
   */
  public hexToPixel(hex: AxialCoordinate): { x: number; y: number } {
    const offset = this.getGridOffset();
    const pixel = this.hexGrid.toPixel(hex);

    return {
      x: pixel.x + offset.x,
      y: pixel.y + offset.y
    };
  }

  /**
   * Get hex corner points at a pixel location
   * Required for terrain grid effects
   */
  protected getHexCornersAtPixel(center: { x: number; y: number }): { x: number; y: number }[] {
    const corners: { x: number; y: number }[] = [];
    const isIsometric = this.config.hexGridConfig.projection === 'isometric';

    // Calculate 6 corners of hexagon
    for (let i = 0; i < 6; i++) {
      const angleDeg = 60 * i;
      const angleRad = (Math.PI / 180) * angleDeg;
      let x = this.hexGrid.hexSize * Math.cos(angleRad);
      let y = this.hexGrid.hexSize * Math.sin(angleRad);

      // Apply isometric projection to corners if enabled (must match getHexVertices)
      if (isIsometric) {
        const x_iso = (x - y) * 0.5;
        const y_iso = (x + y) * 0.25;
        x = x_iso;
        y = y_iso;
      }

      corners.push({
        x: center.x + x,
        y: center.y + y
      });
    }

    return corners;
  }

  // =========================================
  // Camera Control Methods (L4-ZOOM-005)
  // =========================================

  /**
   * Get the camera manager for external control
   * Used by React components to attach event handlers
   */
  public getCameraManager(): CameraManager | null {
    return this.cameraManager;
  }

  /**
   * Get the terrain renderer (dev tool: background position editor)
   */
  public getTerrainRenderer(): TerrainRenderer | null {
    return this.terrainRenderer;
  }

  /**
   * Get all gameplay containers (grid, creatures, effects, highlights, coordinates).
   * Dev tool: used by GridPositionEditor to move the grid relative to the background.
   */
  public getGameplayContainers(): PIXI.Container[] {
    const containers: PIXI.Container[] = [
      this.gridContainer,
      this.highlightLayer,
      this.creatureLayer,
      this.coordinateLayer,
    ];
    if (this.terrainRenderer) {
      containers.push(this.terrainRenderer.getEffectsContainer());
      containers.push(this.terrainRenderer.getDustContainer());
    }
    return containers;
  }

  /**
   * Get the world container for adding elements that should follow camera transforms
   * Used by CombatVisualizationManager to add effect layers (health bars, damage numbers, etc.)
   */
  public getWorldContainer(): PIXI.Container {
    return this.worldContainer;
  }

  /**
   * Zoom in by one step (for button clicks)
   */
  public zoomIn(): void {
    if (this.cameraManager) {
      this.cameraManager.zoomIn();
      this.cameraManager.applyTransform();
    }
  }

  /**
   * Zoom out by one step (for button clicks)
   */
  public zoomOut(): void {
    if (this.cameraManager) {
      this.cameraManager.zoomOut();
      this.cameraManager.applyTransform();
    }
  }

  /**
   * Zoom to a specific point (for mouse wheel)
   */
  public zoomAtPoint(targetZoom: number, screenPos: Point): void {
    if (this.cameraManager) {
      this.cameraManager.zoomAtPoint(targetZoom, screenPos);
      this.cameraManager.applyTransform();
    }
  }

  /**
   * Pan the camera (for WASD keys)
   */
  public pan(deltaX: number, deltaY: number): void {
    if (this.cameraManager) {
      this.cameraManager.pan(deltaX, deltaY);
      this.cameraManager.applyTransform();
    }
  }

  /**
   * Reset camera to default state
   */
  public resetCamera(): void {
    if (this.cameraManager) {
      this.cameraManager.reset();
      this.cameraManager.applyTransform();
    }
  }

  /**
   * Get current zoom level
   */
  public getZoom(): number {
    return this.cameraManager?.getZoom() ?? 1.0;
  }

  /**
   * Normalize image URL to ensure proper format for PixiJS
   * Converts raw base64 data to data URI format (required for challenge creature sprites)
   */
  protected normalizeImageUrl(url: string): string {
    // Already a data URI
    if (url.startsWith('data:')) {
      return url;
    }

    // Already a HTTP(S) URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Already a relative path starting with /
    if (url.startsWith('/')) {
      return url;
    }

    // Assume raw base64 data - convert to data URI
    // Detect image format from base64 header
    let mimeType = 'image/png'; // default
    if (url.startsWith('/9j/')) {
      mimeType = 'image/jpeg';
    } else if (url.startsWith('R0lGOD')) {
      mimeType = 'image/gif';
    } else if (url.startsWith('PHN2Zy')) {
      mimeType = 'image/svg+xml';
    }

    console.log(`[${this.constructor.name}] Converting raw base64 to data URI:`, {
      detectedType: mimeType,
      base64Preview: url.substring(0, 20)
    });

    return `data:${mimeType};base64,${url}`;
  }
}
